import {Injectable} from '@angular/core'
import {HttpClient, HttpParams, HttpErrorResponse} from '@angular/common/http'

import {Observable} from 'rxjs'
import {tap, map} from 'rxjs/operators'

import {Template} from './template.interface'
import {AdvancedConfiguration} from '@app/core/services/rest-api.model'
import {
  ApiSearchRequest,
  ApiSearchResponse,
} from '@app/core/services/api.interface'
import {LoggerService} from '@app/core/services/logger.service'
import {ContextService} from '@app/core/services/context.service'

export {Template}

export interface GetTemplatesRequest extends ApiSearchRequest {
  domain?: string
}
export interface GetTemplatesResponse extends ApiSearchResponse<Template> {}

@Injectable()
export class TemplateService {
  constructor(
    private http: HttpClient,
    private contextSvc: ContextService,
    private logger: LoggerService,
  ) {}

  public getTemplates(
    request?: GetTemplatesRequest,
  ): Observable<GetTemplatesResponse> {
    if (!request) {
      request = {}
    }

    let domain = ''

    if (request.domain) {
      domain = request.domain
    } else {
      domain = this.contextSvc.activeDomain
    }

    // /Domains/{domain}/ConfigTemplates
    const url = 'api://Domains/' + domain + '/Templates'

    // convert all object properties to strings
    let params = new HttpParams()
    if (request.search) {
      params = params.set('search', request.search)
    }
    if (request.sort) {
      params = params
        .set('sort', request.sort)
        .set('asc', !request.asc ? 'false' : 'true')
    }

    // TODO: workaround for backend problem!
    if (request.offset) {
      params = params.set('offset', request.offset.toString())
    } else {
      if (request.results) {
        params = params.set('offset', '0')
      }
    }

    if (request.results) {
      params = params.set('results', request.results.toString())
    }

    return this.http.get<GetTemplatesResponse>(url, {params: params}).pipe(
      tap(
        (res) => {
          this.logger.log(res)
        },
        (err: Error) => {
          this.handleError(err)
        },
      ),
    )
  }

  public getTemplate(domain: string, code: number): Observable<Template> {
    const url = 'api://Domains/' + domain + '/Templates/' + code
    return this.http.get<Template>(url).pipe(
      tap(
        (res) => {
          this.logger.log(res)
        },
        (err: Error) => {
          this.handleError(err)
        },
      ),
    )
  }

  public postTemplate(
    domain: string,
    template: Template,
  ): Observable<Template> {
    const url = 'api://Domains/' + domain + '/Templates/'
    return this.http.post<Template>(url, template).pipe(
      tap(
        (res) => {
          this.logger.log(res)
        },
        (err: Error) => {
          this.handleError(err)
        },
      ),
    )
  }

  public putTemplate(
    domain: string,
    code: number,
    template: Template,
  ): Observable<Template> {
    const url = 'api://Domains/' + domain + '/Templates/' + code
    return this.http.put<Template>(url, template).pipe(
      tap(
        (res) => {
          this.logger.log(res)
        },
        (err: Error) => {
          this.handleError(err)
        },
      ),
    )
  }

  public deleteTemplate(domain: string, code: number): Observable<void> {
    const url = 'api://Domains/' + domain + '/Templates/' + code
    return this.http.delete<void>(url).pipe(
      tap(
        () => {
          this.logger.log('deleted template')
        },
        (err: Error) => {
          this.handleError(err)
        },
      ),
    )
  }

  public getTemplateConfig(
    domain: string,
    code: number,
  ): Observable<AdvancedConfiguration> {
    const url =
      'api://Domains/' + domain + '/Templates/' + code + '/Configuration'
    return this.http.get<any>(url).pipe(
      tap(
        (res) => {
          this.logger.log(res)
        },
        (err: Error) => {
          this.handleError(err)
        },
      ),
      map((item) => {
        return item.advanced
      }),
    )
  }

  public putTemplateConfig(
    domain: string,
    code: number,
    config: AdvancedConfiguration,
  ): Observable<AdvancedConfiguration> {
    const url =
      'api://Domains/' +
      domain +
      '/Templates/' +
      code +
      '/Configuration/AdvancedSettings'
    return this.http.put<AdvancedConfiguration>(url, config).pipe(
      tap(
        (res) => {
          this.logger.log(res)
        },
        (err: Error) => {
          this.handleError(err)
        },
      ),
    )
  }

  public deleteTemplateConfig(
    domain: string,
    code: number,
    key: string,
  ): Observable<void> {
    const url =
      'api://Domains/' + domain + '/Templates/' + code + '/Configuration/' + key
    return this.http.delete<void>(url).pipe(
      tap(
        () => {
          this.logger.log('deleted setting')
        },
        (err: Error) => {
          this.handleError(err)
        },
      ),
    )
  }

  private handleError(err: Error) {
    if (err instanceof HttpErrorResponse) {
      if (err.error) {
        throw new Error(err.error.description)
      } else {
        throw new Error('Error: ' + err.status + ' => ' + err.message)
      }
    }
  }
}
